import { UserRole } from "./user";

export interface Organization {
  lassoOrganizationId: string;
  created: string;
  lassoId: string;
  name: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  billingResponsibleName: string | null;
  billingResponsibleEmail: string;
  billingResponsiblePhone: string | null;
  signerName: string | null;
  signerEmail: string;
  signerPhone: string | null;
  address1: string;
  address2: string;
  city: string;
  zipCode: string;
  creditCardOnFile: boolean;
  partnerName: string;
  partnerId: string;
  isPartner: boolean;
  parentOrgId: string | null;
  parentOrgName: string | null;
  primaryOrgId: string | null;
  primaryOrgName: string | null;
  responsibleId: string | null;
  responsibleName: string | null;
  isDataPartner: boolean;
  isSalesPartner: boolean;
  restrictPurchasesToAdmins: boolean;
  activeUsers: number;
  canCreateTrial: boolean;
  currentSolution: Subscription;
  nextSolution: unknown | null;
  previousSolution: Subscription | null;
  currentLicenses: License;
  nextLicenses: unknown | null;
  contracts: Contract[];
  contentProviderSettings: unknown[];
  apiKey: string | null;
  termsAccepted: boolean;
  freemiumTermsAccepted: boolean;
  marketingAccepted: boolean;
  privacyPolicyAccepted: boolean;
  extraBillingInfo: string;
  organizationType: string;
}

export interface License {
  amount: number;
  fromDate: string;
  toDate: string | null;
}

export interface Subscription {
  id: number | null;
  module: unknown;
  trial: boolean | null;
  productName: string;
  productUniqueName: string;
  versionName: string;
  versionUniqueName: string;
  ownCredentials: boolean;
  type: SubscriptionType;
  fromDate: string;
  toDate: string | null;
  commitmentDuration: number;
  pricePerLicense: boolean | null;
  price: number | null;
  activeCreditCardSubscription: boolean;
  stripeSubscriptionId: string;
  isActive: boolean;
  hasEndDate: boolean;
  salesPartnerShare: number | null;
  contentProviderShare: number | null;
}

export enum SubscriptionType {
  App = "App",
  Solution = "Solution",
}

export interface Contract {
  id: number;
  uniqueId: string;
  lassoOrganizationId: string;
  features: ContractFeature[];
  solutionVersion: unknown;
  licenses: number;
  pricePerLicense: number | null;
  cvr: number;
  phone: string;
  email: string;
  contactName: string;
  contactEmail: string;
  billingEmail: string;
  billingEAN: string;
  created: string;
  updated: string | null;
  subscriptionStartDate: string;
  activationDate: string;
  firstBillDate: string;
  sendTime: string | null;
  cancellationDate: string | null;
  offerExpiration: string | null;
  price: number;
  vatExcluded: boolean;
  commitmentDuration: number;
  billingFrequency: number;
  firstDurationDiscount: number;
  discountPeriodCount: number | null;
  paymentTerms: string;
  internalNote: string;
  doNotInvoice: boolean;
  lassoShare: number | null;
  accepted: boolean;
  acceptTime: string | null;
  salesPartnerUserId: string;
  extraLineItems: string[];
  additions: ContractAddition[];
  pendingLicenseAddition: boolean;
  customText: string;
  signedPDFLinks: unknown[];
}

export interface ContractAddition {
  id: number;
  extraLineItems: string[];
  paymentTerms: string;
  fromDate: string;
  activationDate: string | null;
  price: number | null;
  permanentDiscount: number | null;
  licenses: number;
  features: ContractFeature[];
  customText: string;
  sendTime: string | null;
  acceptTime: string | null;
  offerExpiration: string | null;
  salesPartnerUserId: string;
  contactName: string;
  contactEmail: string;
}

export interface ContractFeature {
  excludeFromInvoice: boolean;
  feature: unknown;
}

export interface User {
  name: string;
  userName: string;
  id: string;
  lassoOrganizationId: string;
  organization: Organization;
  email: string;
  emailConfirmed: boolean;
  phoneNumber: string | null;
  created: string;
  active: boolean;
  isPasswordSet: boolean;
  appLicenses: unknown[];
  roles: UserRole[];
  productUserLinks: unknown[];
  settings: UserSettings;
  twoFactorAuthenticationEnabled: boolean;
}

export interface UserSettings {
  firstTimeLogin: number;
  lastVisit: number;
  previousVisit: number;
  exportInput: {
    Email: {
      email: string;
    };
  };
  lastProductUpdateCheck: string;
}

export type FeatureModel = {
  id: number;
  name: string;
  uniqueName: Feature;
  derivedFeatures: unknown[];
  disabled: boolean;
  groupName: string | null;
  hasApp: boolean;
  hasExport: boolean;
  iconName: string | null;
  linkToInformation: string | null;
  sortOrder: number | null;
  subGroupName: string | null;
};

export enum Feature {
  Export20000 = "Export20000",
  Export50000 = "Export50000",
  Export100000 = "Export100000",
  ExportUnlimited = "ExportUnlimited",
  Paqle = "Paqle",
  TrueOwnerReport = "TrueOwnerReport",
  MultipleSessions = "MultipleSessions",
  PrivateProfessionals = "PrivateProfessionals",
  TeleproviderData = "TeleproviderData",
  TeleproviderNumberLookup = "TeleproviderNumberLookup",
  ExportTrial100 = "ExportTrial100",
  ExportTrial1000 = "ExportTrial1000",
  ExportTrial10000 = "ExportTrial10000",
  OwnershipDiagramEditing = "OwnershipDiagramEditing",
  RiskToleranceObservation = "RiskToleranceObservation",
  Creditsafe50Month = "Creditsafe50Month",
  Creditsafe100Month = "Creditsafe100Month",
  Creditsafe200Month = "Creditsafe200Month",
  Creditsafe300Month = "Creditsafe300Month",
  Creditsafe400Month = "Creditsafe400Month",
  Creditsafe500Month = "Creditsafe500Month",
  Creditsafe50Year = "Creditsafe50Year",
  Creditsafe100Year = "Creditsafe100Year",
  Creditsafe200Year = "Creditsafe200Year",
  Creditsafe300Year = "Creditsafe300Year",
  Creditsafe400Year = "Creditsafe400Year",
  Creditsafe500Year = "Creditsafe500Year",
  Creditsafe750Year = "Creditsafe750Year",
  Creditsafe1000Year = "Creditsafe1000Year",
  Agriculture = "Agriculture",
  Technologies = "Technologies",
  FixedOutgoingIP = "FixedOutgoingIP",
  AuthorizationRegistry = "AuthorizationRegistry",
  GooglePhoneLookup = "GooglePhoneLookup",
  Dynamics365QuickCreateForAll = "Dynamics365QuickCreateForAll",
  AccountantInformationExport = "AccountantInformationExport",
  TrueOwnerExport = "TrueOwnerExport",
  TrueOwnerValidationExport = "TrueOwnerValidationExport",
  ObservationsExport = "ObservationsExport",
  AccountantIndepdendencyExport = "AccountantIndepdendencyExport",
  AccountantIndependencyManualExport = "AccountantIndependencyManualExport",
  PEPExport = "PEPExport",
  BasicInfoExport = "BasicInfoExport",
  BasicInfoAndReportExport = "BasicInfoAndReportExport",
  LastFiveYearsReportsExport = "LastFiveYearsReportsExport",
  LastFiveYearsReportsTabbedExport = "LastFiveYearsReportsTabbedExport",
  CompanyPeopleExport = "CompanyPeopleExport",
  ProductionUnitsExport = "ProductionUnitsExport",
  CrawlerContactsExport = "CrawlerContactsExport",
  TeledataExport = "TeledataExport",
  TrueOwnerExtendedExport = "TrueOwnerExtendedExport",
  DirectOwnershipExport = "DirectOwnershipExport",
  ResumeAPI = "ResumeAPI",
  ProspectingCampaigns1 = "ProspectingCampaigns1",
  ProspectingCampaigns2 = "ProspectingCampaigns2",
  ProspectingCampaigns5 = "ProspectingCampaigns5",
  ProspectingCampaigns10 = "ProspectingCampaigns10",
  ProspectingCampaigns15 = "ProspectingCampaigns15",
  ProspectingCampaigns20 = "ProspectingCampaigns20",
  ProspectingCampaigns25 = "ProspectingCampaigns25",
  ProspectingCampaigns50 = "ProspectingCampaigns50",
  ProspectingCampaignsUnlimited = "ProspectingCampaignsUnlimited",
  ProspectingMaxLeads5 = "ProspectingMaxLeads5",
  ProspectingMaxLeads10 = "ProspectingMaxLeads10",
  ProspectingMaxLeads20 = "ProspectingMaxLeads20",
  ProspectingMaxLeads25 = "ProspectingMaxLeads25",
  ProspectingMaxLeads50 = "ProspectingMaxLeads50",
  ProspectingMaxLeads75 = "ProspectingMaxLeads75",
  ProspectingMaxLeads100 = "ProspectingMaxLeads100",
  ProspectingMaxLeads150 = "ProspectingMaxLeads150",
  ProspectingMaxLeads200 = "ProspectingMaxLeads200",
  ProspectingMaxLeads250 = "ProspectingMaxLeads250",
  ProspectingMaxLeads300 = "ProspectingMaxLeads300",
  ProspectingMaxLeads400 = "ProspectingMaxLeads400",
  ProspectingAllFields = "ProspectingAllFields",
  ProspectingIntegration = "ProspectingIntegration",
  ProspectingEmail = "ProspectingEmail",
  ReportAnalysis = "ReportAnalysis",
  OwnershipDiagramHistoric = "OwnershipDiagramHistoric",
  TinglysningCertificateUpload = "TinglysningCertificateUpload",
  HistoricPropertiesExport = "HistoricPropertiesExport",
  ProductionUnitFromPNumberExport = "ProductionUnitFromPNumberExport",
  LiabilitiesExport = "LiabilitiesExport",
  RatingExport = "RatingExport",
  AccountantCustomersExport = "AccountantCustomersExport",
  VaekstFondenBusinessAngelsExport = "VaekstFondenBusinessAngelsExport",
  CompanyStatusHistoryExport = "CompanyStatusHistoryExport",
  StarkFullCVRExport = "StarkFullCVRExport",
  RatingComparisonExport = "RatingComparisonExport",
  PrivateProfessionalsExport = "PrivateProfessionalsExport",
  CompanyHistoryExport = "CompanyHistoryExport",
  EmployeeHistoryExport = "EmployeeHistoryExport",
  PropertiesAndLivestockExport = "PropertiesAndLivestockExport",
  BeierholmAccountingNotesExport = "BeierholmAccountingNotesExport",
  LastFiveYearsReportsAccountingNotesExport = "LastFiveYearsReportsAccountingNotesExport",
  DownwardOwnershipExport = "DownwardOwnershipExport",
  SubmortgageExport = "SubmortgageExport",
  ContactMetadataExport = "ContactMetadataExport",
  ParkingUnitsExport = "ParkingUnitsExport",
  ValuationExport = "ValuationExport",
  CompanyAuthorizationsExport = "CompanyAuthorizationsExport",
  CrawlerContactsBasicInformationExport = "CrawlerContactsBasicInformationExport",
  AccountantBasicApp = "AccountantBasicApp",
  AnalysisBasicApp = "AnalysisBasicApp",
  BenchmarkingBasicApp = "BenchmarkingBasicApp",
  BIQTimelineBasicApp = "BIQTimelineBasicApp",
  BisnodeCompanyRelationsBasicApp = "BisnodeCompanyRelationsBasicApp",
  BisnodeCreditRatingBasicApp = "BisnodeCreditRatingBasicApp",
  BisnodeMarketViewbasicApp = "BisnodeMarketViewbasicApp",
  BisnodeTargetGroupBasicApp = "BisnodeTargetGroupBasicApp",
  BusinessAdvisorBasicApp = "BusinessAdvisorBasicApp",
  CompanyInsightApp = "CompanyInsightApp",
  ContactsBasicApp = "ContactsBasicApp",
  CVRHistoryBasicApp = "CVRHistoryBasicApp",
  CVRKeyFiguresBasicApp = "CVRKeyFiguresBasicApp",
  CVRNetworkBasicApp = "CVRNetworkBasicApp",
  CVROverviewBasicApp = "CVROverviewBasicApp",
  CVROwnersBasicApp = "CVROwnersBasicApp",
  CVROwnershipBasicApp = "CVROwnershipBasicApp",
  CVRTargetgroupBasicApp = "CVRTargetgroupBasicApp",
  DashboardBasicApp = "DashboardBasicApp",
  DebitorlistenBasicApp = "DebitorlistenBasicApp",
  EconomicBasicApp = "EconomicBasicApp",
  ExporterBasicApp = "ExporterBasicApp",
  FinanceBasicApp = "FinanceBasicApp",
  FirmabasisBasicApp = "FirmabasisBasicApp",
  GiownershipsApp = "GiownershipsApp",
  GooglerouteplannerBasicApp = "GooglerouteplannerBasicApp",
  GrowthratingBasicApp = "GrowthratingBasicApp",
  HistoryBasicApp = "HistoryBasicApp",
  HousingCoopBasicApp = "HousingCoopBasicApp",
  InformationLimitedApp = "InformationLimitedApp",
  InformationBasicApp = "InformationBasicApp",
  InternalTasksBasicApp = "InternalTasksBasicApp",
  KamBasicApp = "KamBasicApp",
  KOBOpenadvancedBasicApp = "KOBOpenadvancedBasicApp",
  KOBOpenBasicApp = "KOBOpenBasicApp",
  KOBOpenBusinessApp = "KOBOpenBusinessApp",
  KOBOpenCreditApp = "KOBOpenCreditApp",
  KOBOpenOwnershipApp = "KOBOpenOwnershipApp",
  LabelmakerBasicApp = "LabelmakerBasicApp",
  LassoSalesReportBasicApp = "LassoSalesReportBasicApp",
  MonitoringBasicApp = "MonitoringBasicApp",
  NewsBasicApp = "NewsBasicApp",
  NPSTodayBasicApp = "NPSTodayBasicApp",
  ObservationsBasicApp = "ObservationsBasicApp",
  OwnershipDiagramBasicApp = "OwnershipDiagramBasicApp",
  PropertiesBasicApp = "PropertiesBasicApp",
  PropertiesFreeApp = "PropertiesFreeApp",
  ProspectingBasicApp = "ProspectingBasicApp",
  RatingBasicApp = "RatingBasicApp",
  ReportingBasicApp = "ReportingBasicApp",
  ResidenceBasicApp = "ResidenceBasicApp",
  RiskAssessmentBasicApp = "RiskAssessmentBasicApp",
  RiskAssessmentFreeApp = "RiskAssessmentFreeApp",
  SalesBasicApp = "SalesBasicApp",
  SearchBasicApp = "SearchBasicApp",
  TinglysningBasicApp = "TinglysningBasicApp",
  TrueOwnersBasicApp = "TrueOwnersBasicApp",
  TruePropertyOwnersBasicApp = "TruePropertyOwnersBasicApp",
  TwinFinderBasicApp = "TwinFinderBasicApp",
  API = "API",
  Ritzau = "Ritzau",
  PEP = "PEP",
  TrueOwnerValidation = "TrueOwnerValidation",
  AdversusIntegration = "AdversusIntegration",
  EnreachCampaignsIntegration = "EnreachCampaignsIntegration",
  EnreachOutboundIntegration = "EnreachOutboundIntegration",
  ZendeskSellIntegration = "ZendeskSellIntegration",
  PipedriveIntegration = "PipedriveIntegration",
  DynamicsCRMIntegration = "DynamicsCRMIntegration",
  WebCRMIntegration = "WebCRMIntegration",
  WebCRMB2CIntegration = "WebCRMB2CIntegration",
  LeadDeskIntegrtation = "LeadDeskIntegrtation",
  SalesforceIntegration = "SalesforceIntegration",
  HubSpotIntegration = "HubSpotIntegration",
  CustomAPI1Integration = "CustomAPI1Integration",
  CustomAPI2Integration = "CustomAPI2Integration",
  CustomAPI3Integration = "CustomAPI3Integration",
  ZohoCRMIntegration = "ZohoCRMIntegration",
  MondayIntegration = "MondayIntegration",
  BlueNordixIntegration = "BlueNordixIntegration",
  SuperOfficeIntegration = "SuperOfficeIntegration",
  CloseCRMIntegration = "CloseCRMIntegration",
  DocumentStorage = "DocumentStorage",
  MortgageExport = "MortgageExport",
  TwinExport = "TwinExport",
  MarkmanCustomExport = "MarkmanCustomExport",
  LassoSalesCredits20 = "LassoSalesCredits20",
  LassoSalesCredits100 = "LassoSalesCredits100",
  LassoSalesCredits200 = "LassoSalesCredits200",
  LassoSalesCredits300 = "LassoSalesCredits300",
  LassoSalesCredits400 = "LassoSalesCredits400",
  LassoSalesCredits500 = "LassoSalesCredits500",
  LassoSalesCredits600 = "LassoSalesCredits600",
  LassoSalesCredits700 = "LassoSalesCredits700",
  LassoSalesCredits800 = "LassoSalesCredits800",
  LassoSalesCredits900 = "LassoSalesCredits900",
  LassoSalesCredits1000 = "LassoSalesCredits1000",
  LassoSalesCredits1500 = "LassoSalesCredits1500",
  LassoSalesCredits2000 = "LassoSalesCredits2000",
  LassoSalesCredits2500 = "LassoSalesCredits2500",
  LassoSalesCredits3500 = "LassoSalesCredits3500",
  LassoSalesCredits5000 = "LassoSalesCredits5000",
  LassoSalesCredits10000 = "LassoSalesCredits10000",
  LassoSalesFreemium = "LassoSalesFreemium",
  LassoSalesEssentials = "LassoSalesEssentials",
  LassoSalesStandard = "LassoSalesStandard",
  LassoSalesPro = "LassoSalesPro",
}
